import React, { useState } from "react";
import { Table, Input, Space, Avatar, Tag, Modal, Button, Form, message, Menu, Dropdown, Row, Col, Card, Typography, Divider, DatePicker, Descriptions, Select } from "antd";
import { SearchOutlined, UserOutlined, MoreOutlined, PlusOutlined, MailOutlined, LockOutlined, EnvironmentOutlined, PhoneOutlined, ReloadOutlined, TeamOutlined, RiseOutlined, FallOutlined, DollarOutlined, SettingOutlined, EyeOutlined } from "@ant-design/icons";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import user from "../../services/user"; 
import auth from "../../services/auth"; 
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form as AntForm } from "antd";
import { useNavigate } from "react-router-dom";
import { loginStart, loginSuccess, loginFailure, setOriginalAdminUser, clearOriginalAdminUser } from '../../redux/userSlice';
import { useDispatch, useSelector } from 'react-redux';

const { RangePicker } = DatePicker;

const Users = () => {
  const [loading, setLoading] = useState(false);
  const [searchFields, setSearchFields] = useState({ name: '', email: '', phone: '', address: '' });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddUserModalVisible, setIsAddUserModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [registerForm] = Form.useForm();
  const queryClient = useQueryClient();
  const [formSearch] = AntForm.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state) => state.user);
  const [dateRange, setDateRange] = useState(null);
  const { data: usersData = [], isLoading, refetch } = useQuery({
    queryKey: ['users', searchFields, dateRange],
    queryFn: async () => {
      const params = {};
      if (searchFields.name) params.name = searchFields.name;
      if (searchFields.email) params.email = searchFields.email;
      if (searchFields.phone) params.phone = searchFields.phone;
      if (searchFields.address) params.address = searchFields.address;
      if (dateRange) {
        params.start_date = dateRange[0].format('YYYY-MM-DD');
        params.end_date = dateRange[1].format('YYYY-MM-DD');
      }
      const response = await user.get(params);
      return response.data;
    }
  });
  const [leadDetailModalOpen, setLeadDetailModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [isLeadDetailsModalVisible, setIsLeadDetailsModalVisible] = React.useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = React.useState(null);

  const updateDailyLimitMutation = useMutation({
    mutationFn: ({ userId, limit }) => user.updateDailyLimit(userId, limit),
    onSuccess: () => {
      message.success('Daily purchase limit updated successfully');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to update daily purchase limit');
    }
  });

  const toggleLockMutation = useMutation({
    mutationFn: (userId) => auth.toggleLockUser(userId),
    onSuccess: (data) => {
      message.success(data.message);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      message.error(error.response.data.message);
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId) => auth.deleteUser(userId),
    onSuccess: () => {
      message.success("User deleted successfully");
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      message.error(error.response.data.message);
    }
  });

  const addUserMutation = useMutation({
    mutationFn: (userData) => auth.register(userData),
    onSuccess: () => {
      message.success('User added successfully!');
      setIsAddUserModalVisible(false);
      registerForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      message.error(error.response.data.message);
    }
  });

  const handleMenuClick = async (key, record) => {
    try {
      setLoading(true);
      switch (key) {
        case "viewAs":
          await viewAs(record.id);
          break;
        case "viewProfile":
          showModal(record);
          break;
        case "toggleLock":
          await toggleLockMutation.mutateAsync(record.id);
          break;
        case "deleteUser":
          Modal.confirm({
            title: 'Are you sure you want to delete this user?',
            content: 'This action cannot be undone. All the associated data will be removed',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: () => deleteUserMutation.mutateAsync(record.id),
          });
          break;
        default:
          break;
      }
    } catch (error) {
      console.error("Error processing request:", error);
      message.error(error.response.data.message);
    } finally {
      setLoading(false);
    }
  };

  const viewAs = async (values) => {
    setLoading(true);
    dispatch(loginStart());

    try {
      dispatch(setOriginalAdminUser(currentUser));
      
      const response = await auth.viewAs(values);
      setLoading(false);
      // if (response.data.is_locked) {
      //   message.error('Your account is locked. Please contact support.');
      //   dispatch(loginFailure());
      //   return;
      // }
      const token = response.data.token;
      localStorage.setItem('token', token);
      const dashboardUrl = '/dashboard';
      navigate(dashboardUrl);
      dispatch(loginSuccess(response.data));
      message.success('Login successful');
    } catch (error) {
      if (error.response && error.response.status === 422) {
        message.error('User not found');
      } else {
        message.error('Login failed. Please try again later.');
      }
      setLoading(false);
      dispatch(loginFailure());
      dispatch(clearOriginalAdminUser());
    }
  };

  const menu = (record) => (
    <Menu onClick={({ key }) => handleMenuClick(key, record)}>
      {record.role !== "admin" && (
        <>
          <Menu.Item key="toggleLock">{record.is_locked === "1" ? "Unlock User" : "Lock User"}</Menu.Item>
          <Menu.Item key="deleteUser">Delete User</Menu.Item>
          <Menu.Item key="viewAs">View As</Menu.Item>
        </>
      )}
    </Menu>
  );

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar
            style={{
              background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
              color: '#fff',
              fontWeight: 700
            }}
            size={32}
          >
            {`${(record.name?.[0] || '').toUpperCase()}${(record.last_name?.[0] || '').toUpperCase()}` || <UserOutlined />}
          </Avatar>
          <span>{record.name} {record.last_name}</span>
        </Space>
      ),
    },
    {
      title: 'Last',
      dataIndex: 'last_name',
      key: 'last_name',
      responsive: ['md'],
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      responsive: ['md'],
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      responsive: ['lg'],
    },
    {
      title: 'Total Purchases',
      key: 'total_purchases',
      render: (text, record) => record.lead_purchases ? record.lead_purchases.length : 0,
    },
    {
      title: 'Daily Purchase Limit',
      dataIndex: 'daily_purchase_limit',
      key: 'daily_purchase_limit',
      render: (limit, record) => (
        <Space>
          <Select
            value={limit || 3}
            style={{ width: 100 }}
            onChange={(value) => {
              user.updateDailyLimit(record.id, value)
                .then(() => {
                  message.success('Daily purchase limit updated successfully');
                  queryClient.invalidateQueries({ queryKey: ['users'] });
                })
                .catch((error) => {
                  message.error(error.response?.data?.message || 'Failed to update daily purchase limit');
                });
            }}
            options={Array.from({ length: 100 }, (_, i) => ({
              value: i + 1,
              label: `${i + 1} leads`
            }))}
          />
          {/* <Tag type="secondary">
            Today: {record.today_purchase_count || 0}
          </Tag> */}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (text, record) => (
        <Tag color={record.is_locked === "1" ? "red" : "green"} style={{ fontWeight: 500 }}>
          {record.is_locked === "1" ? "Locked" : "Unlocked"}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Dropdown overlay={menu(record)} trigger={['click']}>
          <Button type="link" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const handleFieldChange = (field, value) => {
    setSearchFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = (values) => {
    setSearchFields(values);
    refetch();
  };

  const handleReset = () => {
    formSearch.resetFields();
    setSearchFields({ name: '', email: '', phone: '', address: '' });
    setDateRange(null);
    refetch();
  };

  const showModal = (user) => {
    console.log("selectedUser", user);
    setSelectedUser(user);
    form.setFieldsValue(user);
    setIsModalVisible(true);
  };

  const handleAddUser = (values) => {
    addUserMutation.mutate(values);
  };

  const showLeadDetails = (lead) => {
    setSelectedLeadDetails(lead);
    setIsLeadDetailsModalVisible(true);
  };

  const expandedRowRender = (record) => {
    return (
      <div style={{ padding: '16px' }}>
      <Table
        columns={[
          {
              title: 'Lead Name',
              dataIndex: ['lead', 'first_name'],
              key: 'lead_name',
              render: (_, record) => `${record.lead.first_name} ${record.lead.last_name}`
            },
            {
              title: 'Email',
              dataIndex: ['lead', 'email'],
              key: 'email'
            },
            {
              title: 'Phone',
              dataIndex: ['lead', 'phone'],
              key: 'phone'
            },
            {
              title: 'State',
              dataIndex: ['lead', 'state_name'],
              key: 'state'
            },
            {
              title: 'Purchase Date',
              dataIndex: 'created_at',
              key: 'purchase_date',
              render: (date) => new Date(date).toLocaleDateString()
            },
            {
              title: 'Amount',
              dataIndex: 'amount',
              key: 'amount',
              render: (amount) => `$${parseFloat(amount).toFixed(2)}`
            },
            {
              title: 'Status',
              dataIndex: 'payment_status',
              key: 'status',
              render: (status) => (
                <Tag color={status ? 'success' : 'warning'}>
                  {status ? 'Paid' : 'Pending'}
                </Tag>
              )
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_, record) => (
              <Button
                type="link"
                  onClick={() => showLeadDetails(record.lead)}
                  icon={<EyeOutlined />}
                >
                  View Details
              </Button>
              )
            }
          ]}
          dataSource={record.lead_purchases}
        pagination={false}
          rowKey="id"
          style={{ 
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
      />
      </div>
    );
  };

  const calculateStats = (data) => {
    const totalUsers = data.length;
    const activeUsers = data.filter(user => !user.is_locked).length;
    const lockedUsers = data.filter(user => user.is_locked).length;
    const totalPurchases = data.reduce((sum, user) => sum + (user.lead_purchases?.length || 0), 0);
    const averagePurchases = totalPurchases / totalUsers;

    return {
      totalUsers,
      activeUsers,
      lockedUsers,
      totalPurchases,
      averagePurchases
    };
  };

  const prepareChartData = () => {
    // Group users by registration date
    const groupedData = usersData.reduce((acc, user) => {
      const date = new Date(user.created_at).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = {
          date,
          users: 0,
          purchases: 0
        };
      }
      acc[date].users += 1;
      acc[date].purchases += user.lead_purchases?.length || 0;
      return acc;
    }, {});

    return Object.values(groupedData).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
  };

  const preparePieData = () => {
    const stats = calculateStats(usersData);
    return [
      { name: 'Active Users', value: stats.activeUsers },
      { name: 'Locked Users', value: stats.lockedUsers }
    ];
  };

  const COLORS = ['#52c41a', '#ff4d4f'];

  const stats = calculateStats(usersData);
  const chartData = prepareChartData();
  const pieData = preparePieData();

  // Add date range change handler
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  return (
    <div className="page-container" style={{
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh',
      padding: '24px'
    }}>
      <div className="page-header" style={{
        marginBottom: '32px',
        padding: '24px',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle at top right, rgba(24,144,255,0.1) 0%, rgba(24,144,255,0) 70%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
            transition: 'transform 0.3s ease'
          }}>
            <UserOutlined style={{ fontSize: '20px', color: '#fff' }} />
          </div>
          <div>
            <Typography.Title level={2} style={{
              margin: 0,
              color: '#1f1f1f',
              background: 'linear-gradient(135deg, #1f1f1f 0%, #434343 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: '24px'
            }}>
              User Management
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
          View, search, and manage all users in the system
        </Typography.Text>
          </div>
        </div>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TeamOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: 500,
                  height: 17
                }}>
                  Total Users
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: '#1890ff',
                  lineHeight: 1.2
                }}>
                  {stats.totalUsers}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <RiseOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: 500,
                  height: 17
                }}>
                  Active Users
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: '#52c41a',
                  lineHeight: 1.2
                }}>
                  {stats.activeUsers}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: 500,
                  height: 17
                }}>
                  Total Purchases
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: '#722ed1',
                  lineHeight: 1.2
                }}>
                  {stats.totalPurchases}
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FallOutlined style={{ fontSize: '24px', color: '#fff' }} />
              </div>
              <div>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  fontWeight: 500,
                  height: 17
                }}>
                  Avg. Purchases/User
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: '#fa8c16',
                  lineHeight: 1.2
                }}>
                  {stats.averagePurchases.toFixed(1)}
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TeamOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={5} style={{ margin: 0 }}>User Growth & Purchases</Typography.Title>
              </div>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                style={{ 
                  borderRadius: '8px',
                  minWidth: '250px'
                }}
                allowClear
                placeholder={['Start Date', 'End Date']}
                format="YYYY-MM-DD"
              />
            </div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line 
                    yAxisId="left"
                    type="monotone" 
                    dataKey="users" 
                    stroke="#1890ff" 
                    name="New Users"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="purchases" 
                    stroke="#52c41a" 
                    name="Purchases"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <TeamOutlined style={{ fontSize: '20px', color: '#fff' }} />
              </div>
              <Typography.Title level={5} style={{ margin: 0 }}>User Status Distribution</Typography.Title>
            </div>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        bordered={false}
        className="shadow-sm"
        style={{ marginBottom: 24, borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)' }}
      >
        <AntForm
          form={formSearch}
          layout="vertical"
          initialValues={searchFields}
          onFinish={handleSearch}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <AntForm.Item name="name" label={<span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}><UserOutlined style={{ color: '#222' }} /> Name</span>}>
                <Input size="large" placeholder="Search by Name" allowClear style={{ borderRadius: 8, minHeight: 48, fontSize: 16 }} prefix={<UserOutlined />} />
              </AntForm.Item>
            </Col>
            <Col xs={24} md={12}>
              <AntForm.Item name="email" label={<span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}><MailOutlined style={{ color: '#222' }} /> Email</span>}>
                <Input size="large" placeholder="Search by Email" allowClear style={{ borderRadius: 8, minHeight: 48, fontSize: 16 }} prefix={<MailOutlined />} />
              </AntForm.Item>
            </Col>
            <Col xs={24} md={12}>
              <AntForm.Item name="phone" label={<span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}><PhoneOutlined style={{ color: '#222' }} /> Phone</span>}>
                <Input size="large" placeholder="Search by Phone" allowClear style={{ borderRadius: 8, minHeight: 48, fontSize: 16 }} prefix={<PhoneOutlined />} />
              </AntForm.Item>
            </Col>
            <Col xs={24} md={12}>
              <AntForm.Item name="address" label={<span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}><EnvironmentOutlined style={{ color: '#222' }} /> Address</span>}>
                <Input size="large" placeholder="Search by Address" allowClear style={{ borderRadius: 8, minHeight: 48, fontSize: 16 }} prefix={<EnvironmentOutlined />} />
              </AntForm.Item>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
          <Col xs={24} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'end', marginTop: 0, gap: 8 }}>
              <Button size="large" type="primary" htmlType="submit" icon={<SearchOutlined />} style={{ borderRadius: 8, minHeight: 48, fontSize: 16 }}>
                Search
              </Button>
              <Button size="large" type="primary" icon={<ReloadOutlined />} onClick={handleReset} style={{ borderRadius: 8, minHeight: 48, fontSize: 16 }}>
                Reset
              </Button>
            </Col>
          </Row>
        </AntForm>
      </Card>
      <Card className="shadow-sm" style={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button 
            size="large"
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => setIsAddUserModalVisible(true)}
            loading={addUserMutation.isLoading}
            style={{ borderRadius: 8 }}
          >
            Add User
          </Button>
        </div>
        <Row>
          <Col span={24}>
            <Table
              size="small"
              columns={columns}
              dataSource={usersData}
              rowKey="id"
              loading={isLoading || 
                toggleLockMutation.isLoading || 
                deleteUserMutation.isLoading
              }
              pagination={{ pageSize: 10 }}
              scroll={{ x: 800 }}
              style={{ borderRadius: 8 }}
              expandable={{
                expandedRowRender,
                rowExpandable: record => record.lead_purchases && record.lead_purchases.length > 0
              }}
            />
          </Col>
        </Row>
      </Card>
    
      <Modal
        title={<span style={{ fontWeight: 600, fontSize: 20 }}>Add User</span>}
        open={isAddUserModalVisible}
        onCancel={() => setIsAddUserModalVisible(false)}
        footer={null}
        bodyStyle={{ padding: 24 }}
        style={{ borderRadius: 12 }}
      >
        <Form
          name="register_form"
          className="register-form"
          onFinish={handleAddUser}
          form={registerForm}
          layout="vertical"
        >
          <Divider style={{ margin: '0 0 16px 0' }} />
          <Form.Item
            name="name"
            label="First Name"
            rules={[
              { required: true, message: 'Please input your first name!' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="First Name"
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[
              { required: true, message: 'Please input your last name!' },
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Last Name"
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone"
            rules={[
              { required: true, message: 'Please input your phone number!' },
            ]}
          >
            <Input
              prefix={<PhoneOutlined />}
              placeholder="Phone"
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            name="address"
            label="Address"
            rules={[
              { required: true, message: 'Please input your address!' },
            ]}
          >
            <Input
              prefix={<EnvironmentOutlined />}
              placeholder="Address"
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { type: 'email', message: 'The input is not a valid email address!' },
              { required: true, message: 'Please input your email!' },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Email"
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please input your password!' },
              { min: 6, message: 'Password must be at least 6 characters long!' },
            ]}
            hasFeedback
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: 'Please confirm your password!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('The two passwords do not match!')
                  );
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Confirm Password"
              size="large"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
          <Form.Item>
            <Button
              loading={loading}
              type="primary"
              htmlType="submit"
              className="register-form-button"
              size="large"
              style={{ borderRadius: 8, width: '100%', fontWeight: 500 }}
            >
              Register
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      {/* Lead Details Modal */}
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <Typography.Title level={4} style={{ margin: 0 }}>Lead Details</Typography.Title>
          </div>
        }
        open={isLeadDetailsModalVisible}
        onCancel={() => setIsLeadDetailsModalVisible(false)}
        footer={null}
        width={800}
        bodyStyle={{ padding: '24px' }}
      >
        {selectedLeadDetails && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card bordered={false} style={{ borderRadius: 8 }}>
                  <Table
                    columns={[
                      { 
                        title: 'Field', 
                        dataIndex: 'field', 
                        key: 'field', 
                        width: '40%',
                        render: (text) => (
                          <Typography.Text strong style={{ color: '#1f1f1f' }}>{text}</Typography.Text>
                        )
                      },
                      { 
                        title: 'Value', 
                        dataIndex: 'value', 
                        key: 'value', 
                        width: '60%',
                        render: (text) => (
                          <Typography.Text style={{ color: '#434343' }}>{text}</Typography.Text>
                        )
                      }
                    ]}
                    dataSource={[
                      { field: 'First Name', value: selectedLeadDetails.first_name },
                      { field: 'Last Name', value: selectedLeadDetails.last_name },
                      { field: 'Phone', value: selectedLeadDetails.phone },
                      { field: 'Email', value: selectedLeadDetails.email },
                      { field: 'Lead Age', value: selectedLeadDetails.lead_old ? new Date(selectedLeadDetails.lead_old).toLocaleDateString() : '-' },
                      { field: 'Last Activity', value: selectedLeadDetails.last_activity },
                      { field: 'State', value: selectedLeadDetails.state_name || selectedLeadDetails.state?.name || 'N/A' },
                      { field: 'Birth Date', value: selectedLeadDetails.birth_date },
                      { field: 'Type of Life Insurance', value: selectedLeadDetails.type_of_life_insurance },
                      { field: 'Coverage Amount', value: selectedLeadDetails.coverage_amount },
                      { field: 'Final Expense Coverage', value: selectedLeadDetails.final_expense_coverage },
                      { field: 'Beneficiary', value: selectedLeadDetails.beneficiary },
                      { field: 'Are you looking for additional retirement income sources?', value: selectedLeadDetails.additional_retirement_income },
                      { field: 'Annual Income', value: selectedLeadDetails.annual_income },
                      { field: 'Do you have interest in an annuity?', value: selectedLeadDetails.guaranteed_account_interest },
                      // { field: 'Can We Contact', value: selectedLeadDetails.can_we_contact },
                      { field: 'Mortgage Protection Amount', value: selectedLeadDetails.mortgage_protection_amount },
                      // { field: 'Funeral Type', value: selectedLeadDetails.funeral_type },
                      // { field: 'Similar Plan Contact Permission', value: selectedLeadDetails.similar_plan_contact_permission },
                      // { field: 'Mortgage Amount Owed', value: selectedLeadDetails.mortgage_amount_owed },
                      { field: 'Lead Price', value: selectedLeadDetails.lead_price },
                      { field: 'Is Public', value: selectedLeadDetails.is_public ? 'Yes' : 'No' },
                    ]}
                    pagination={false}
                    size="small"
                    showHeader={false}
                    rowKey="field"
                    style={{ 
                      margin: '8px 0', 
                      background: '#fafafa', 
                      borderRadius: 8 
                    }}
                  />
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Users;